/**
 * Model colour (Workspace "Meaning colours"). Default: every Account wears the
 * Workspace purple. Optional "per model" mode: every Account gets one of 12
 * hues derived from its id, so a model looks the same in every widget, the
 * chat header and on every device, with nothing to store.
 */
export const MODEL_HUES = [15, 40, 65, 95, 125, 155, 185, 215, 245, 270, 300, 330] as const;

export type ColourMode = 'purple' | 'model';
export const COLOUR_MODES: ColourMode[] = ['purple', 'model'];

/** FNV-1a over the id: stable, cheap and well spread for short ids. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function accountHue(accountId: string): number {
  return MODEL_HUES[hash(String(accountId)) % MODEL_HUES.length]!;
}

export interface AccountColour {
  hue: number | null;
  /** Model name, ring and the bar on chat cards. */
  accent: string;
  /** Header wash behind the model in the chat pane. */
  tint: string;
}

/** The Workspace accent (--ws-accent-2 / --ws-accent). */
export const PURPLE: AccountColour = { hue: null, accent: 'var(--om-accent-2)', tint: 'color-mix(in srgb, var(--om-accent) 12%, transparent)' };

export function colourMode(raw: unknown): ColourMode {
  return raw === 'model' ? 'model' : 'purple';
}

export function accountColour(accountId: string, mode: ColourMode = 'purple'): AccountColour {
  if (mode !== 'model') return PURPLE;
  const hue = accountHue(accountId);
  return { hue, accent: `oklch(0.78 0.12 ${hue})`, tint: `oklch(0.78 0.12 ${hue} / 0.12)` };
}
